import { useEffect, useRef, useCallback } from 'react';

// Memory management utilities for preventing memory leaks and optimizing performance

export interface MemoryCleanupRef {
  cleanup: () => void;
  isCleanedUp: boolean;
}

export class MemoryManager {
  private cleanupFunctions = new Set<() => void>();
  private timers = new Set<NodeJS.Timeout>();
  private intervals = new Set<NodeJS.Timeout>();
  private eventListeners = new Set<{
    element: EventTarget;
    event: string;
    handler: EventListener;
    options?: AddEventListenerOptions;
  }>();
  private observerInstances = new Set<
    IntersectionObserver | MutationObserver | ResizeObserver
  >();
  private abortControllers = new Set<AbortController>();

  // Timer management
  addTimeout(callback: () => void, delay: number): NodeJS.Timeout {
    const timeoutId = setTimeout(() => {
      this.timers.delete(timeoutId);
      callback();
    }, delay);

    this.timers.add(timeoutId);
    return timeoutId;
  }

  addInterval(callback: () => void, delay: number): NodeJS.Timeout {
    const intervalId = setInterval(callback, delay);
    this.intervals.add(intervalId);
    return intervalId;
  }

  clearTimeout(timeoutId: NodeJS.Timeout): void {
    clearTimeout(timeoutId);
    this.timers.delete(timeoutId);
  }

  clearInterval(intervalId: NodeJS.Timeout): void {
    clearInterval(intervalId);
    this.intervals.delete(intervalId);
  }

  // Event listener management
  addEventListener(
    element: EventTarget,
    event: string,
    handler: EventListener,
    options?: AddEventListenerOptions,
  ): void {
    element.addEventListener(event, handler, options);
    this.eventListeners.add({ element, event, handler, options });
  }

  removeEventListener(
    element: EventTarget,
    event: string,
    handler: EventListener,
  ): void {
    element.removeEventListener(event, handler);
    this.eventListeners.forEach(listener => {
      if (
        listener.element === element &&
        listener.event === event &&
        listener.handler === handler
      ) {
        this.eventListeners.delete(listener);
      }
    });
  }

  // Observer management
  addObserver(
    observer: IntersectionObserver | MutationObserver | ResizeObserver,
  ): void {
    this.observerInstances.add(observer);
  }

  removeObserver(
    observer: IntersectionObserver | MutationObserver | ResizeObserver,
  ): void {
    observer.disconnect();
    this.observerInstances.delete(observer);
  }

  // AbortController management
  createAbortController(): AbortController {
    const controller = new AbortController();
    this.abortControllers.add(controller);
    return controller;
  }

  abortController(controller: AbortController): void {
    controller.abort();
    this.abortControllers.delete(controller);
  }

  // Custom cleanup functions
  addCleanupFunction(cleanup: () => void): void {
    this.cleanupFunctions.add(cleanup);
  }

  removeCleanupFunction(cleanup: () => void): void {
    this.cleanupFunctions.delete(cleanup);
  }

  // Cleanup all managed resources
  cleanup(): void {
    // Clear all timers
    this.timers.forEach(timer => clearTimeout(timer));
    this.intervals.forEach(interval => clearInterval(interval));

    // Remove all event listeners
    this.eventListeners.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler);
    });

    // Disconnect all observers
    this.observerInstances.forEach(observer => {
      observer.disconnect();
    });

    // Abort all controllers
    this.abortControllers.forEach(controller => {
      controller.abort();
    });

    // Run custom cleanup functions
    this.cleanupFunctions.forEach(cleanup => {
      try {
        cleanup();
      } catch (error) {
        console.error('Error in cleanup function:', error);
      }
    });

    // Clear all collections
    this.timers.clear();
    this.intervals.clear();
    this.eventListeners.clear();
    this.observerInstances.clear();
    this.abortControllers.clear();
    this.cleanupFunctions.clear();
  }

  // Get current resource counts for monitoring
  getResourceCounts() {
    return {
      timers: this.timers.size,
      intervals: this.intervals.size,
      eventListeners: this.eventListeners.size,
      observers: this.observerInstances.size,
      abortControllers: this.abortControllers.size,
      cleanupFunctions: this.cleanupFunctions.size,
    };
  }
}

// Global memory manager instance
export const globalMemoryManager = new MemoryManager();

// Cleanup when page is about to unload
window.addEventListener('beforeunload', () => {
  globalMemoryManager.cleanup();
});

// React hooks for memory management

/**
 * Hook that provides a memory manager instance for a component
 */
export function useMemoryManager(): MemoryManager {
  const managerRef = useRef<MemoryManager | null>(null);

  if (!managerRef.current) {
    managerRef.current = new MemoryManager();
  }

  useEffect(() => {
    const manager = managerRef.current!;

    return () => {
      manager.cleanup();
    };
  }, []);

  return managerRef.current;
}

/**
 * Hook for managed setTimeout
 */
export function useManagedTimeout(): (
  callback: () => void,
  delay: number,
) => NodeJS.Timeout {
  const manager = useMemoryManager();

  return useCallback(
    (callback: () => void, delay: number) => {
      return manager.addTimeout(callback, delay);
    },
    [manager],
  );
}

/**
 * Hook for managed setInterval
 */
export function useManagedInterval(): (
  callback: () => void,
  delay: number,
) => NodeJS.Timeout {
  const manager = useMemoryManager();

  return useCallback(
    (callback: () => void, delay: number) => {
      return manager.addInterval(callback, delay);
    },
    [manager],
  );
}

/**
 * Hook for managed event listeners
 */
export function useManagedEventListener(
  element: EventTarget | null,
  event: string,
  handler: EventListener,
  options?: AddEventListenerOptions,
): void {
  const manager = useMemoryManager();

  useEffect(() => {
    if (!element) return;

    manager.addEventListener(element, event, handler, options);

    return () => {
      manager.removeEventListener(element, event, handler);
    };
  }, [manager, element, event, handler, options]);
}

/**
 * Hook for managed observers
 */
export function useManagedObserver<
  T extends IntersectionObserver | MutationObserver | ResizeObserver,
>(createObserver: () => T): T | null {
  const manager = useMemoryManager();
  const observerRef = useRef<T | null>(null);

  useEffect(() => {
    const observer = createObserver();
    observerRef.current = observer;
    manager.addObserver(observer);

    return () => {
      manager.removeObserver(observer);
      observerRef.current = null;
    };
  }, [manager, createObserver]);

  return observerRef.current;
}

/**
 * Hook for managed AbortController
 */
export function useManagedAbortController(): AbortController {
  const manager = useMemoryManager();
  const controllerRef = useRef<AbortController | null>(null);

  if (!controllerRef.current) {
    controllerRef.current = manager.createAbortController();
  }

  useEffect(() => {
    const controller = controllerRef.current!;

    return () => {
      manager.abortController(controller);
    };
  }, [manager]);

  return controllerRef.current;
}

/**
 * Hook for cleanup function registration
 */
export function useCleanupFunction(cleanup: () => void): void {
  const manager = useMemoryManager();

  useEffect(() => {
    manager.addCleanupFunction(cleanup);

    return () => {
      manager.removeCleanupFunction(cleanup);
    };
  }, [manager, cleanup]);
}

/**
 * Hook for debounced functions with automatic cleanup
 */
export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number,
): (...args: Parameters<T>) => void {
  const manager = useMemoryManager();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  return useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        manager.clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = manager.addTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay, manager],
  );
}

/**
 * Hook for throttled functions with automatic cleanup
 */
export function useThrottledCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number,
): (...args: Parameters<T>) => void {
  const manager = useMemoryManager();
  const lastCallRef = useRef<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  return useCallback(
    (...args: Parameters<T>) => {
      const now = Date.now();
      const timeSinceLastCall = now - lastCallRef.current;

      if (timeSinceLastCall >= delay) {
        lastCallRef.current = now;
        callback(...args);
      } else {
        if (timeoutRef.current) {
          manager.clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = manager.addTimeout(() => {
          lastCallRef.current = Date.now();
          callback(...args);
        }, delay - timeSinceLastCall);
      }
    },
    [callback, delay, manager],
  );
}

/**
 * Hook to monitor memory usage and detect potential leaks
 */
export function useMemoryMonitoring(
  componentName: string,
  interval: number = 30000,
): void {
  const manager = useMemoryManager();

  useEffect(() => {
    const checkMemoryUsage = () => {
      const resources = manager.getResourceCounts();
      const totalResources = Object.values(resources).reduce(
        (sum, count) => sum + count,
        0,
      );

      if (totalResources > 50) {
        console.warn(
          `Potential memory leak detected in ${componentName}:`,
          resources,
        );
      }

      // Check browser memory if available
      if ('memory' in performance) {
        const memory = (performance as any).memory;
        const memoryUsage = {
          used: Math.round(memory.usedJSHeapSize / 1024 / 1024),
          total: Math.round(memory.totalJSHeapSize / 1024 / 1024),
          limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024),
        };

        if (memoryUsage.used > 100) {
          // More than 100MB
          console.warn(`High memory usage in ${componentName}:`, memoryUsage);
        }
      }
    };

    const intervalId = manager.addInterval(checkMemoryUsage, interval);
    checkMemoryUsage(); // Initial check

    return () => {
      manager.clearInterval(intervalId);
    };
  }, [manager, componentName, interval]);
}

// Utility functions for manual memory management

/**
 * Clean up large objects and arrays
 */
export function cleanupLargeObject(obj: any): void {
  if (obj && typeof obj === 'object') {
    Object.keys(obj).forEach(key => {
      delete obj[key];
    });
  }
}

/**
 * Clear array while preserving reference
 */
export function clearArray<T>(arr: T[]): void {
  arr.length = 0;
}

/**
 * Dispose of DOM references
 */
export function disposeDOMReferences(
  ...refs: Array<React.RefObject<any>>
): void {
  refs.forEach(ref => {
    if (ref.current) {
      ref.current = null;
    }
  });
}

/**
 * Force garbage collection (development only)
 */
export function forceGarbageCollection(): void {
  if (process.env.NODE_ENV === 'development' && 'gc' in window) {
    (window as any).gc();
  }
}
